import type { User } from '../types/user';

type DashboardUser = Pick<User, 'firstName' | 'lastName' | 'agentProfile'> | null | undefined;

/** Only identity already supplied by authentication is used, including for sub-accounts. */
export function dashboardIdentity(user: DashboardUser): string {
  const agency = user?.agentProfile?.agencyName?.trim();
  return agency || [user?.firstName?.trim(), user?.lastName?.trim()].filter(Boolean).join(' ');
}

export function dashboardGreeting(user: DashboardUser, date = new Date()): string {
  const hour = date.getHours();
  const period = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const identity = dashboardIdentity(user);
  return `Good ${period}${identity ? `, ${identity}` : ''}`;
}

/** Delay to the next local greeting boundary; day rollover also changes evening to morning. */
export function nextGreetingBoundaryDelay(date = new Date()): number {
  const next = new Date(date.getTime());
  if (date.getHours() < 12) next.setHours(12, 0, 0, 0);
  else if (date.getHours() < 18) next.setHours(18, 0, 0, 0);
  else { next.setDate(next.getDate() + 1); next.setHours(0, 0, 0, 0); }
  return Math.max(1, next.getTime() - date.getTime());
}

export function canAccessDashboardPayments(user: DashboardUser): boolean {
  const profile = user?.agentProfile;
  return Boolean(profile && (!profile.parentAgentId || profile.canAccessCommissions === true));
}

export type DashboardQuickAction = {
  id: 'new-application' | 'payments' | 'enrolled-students' | 'visa-documents';
  label: string;
  description: string;
  to?: string;
  restricted: boolean;
};

export function dashboardQuickActions(user: DashboardUser): DashboardQuickAction[] {
  const paymentsAllowed = canAccessDashboardPayments(user);
  return [
    { id: 'new-application', label: 'New Application', description: 'Start a student application', to: '/applications/new', restricted: false },
    { id: 'payments', label: 'Payments', description: paymentsAllowed ? 'Commission payments and payout history' : 'Access restricted. Contact your agency administrator.', ...(paymentsAllowed ? { to: '/commissions' } : {}), restricted: !paymentsAllowed },
    { id: 'enrolled-students', label: 'Enrolled Students', description: 'View enrolled student applications', to: '/applications?status=ENROLLED', restricted: false },
    { id: 'visa-documents', label: 'Visa Documents', description: 'Choose an application for visa support', to: '/visa-documents', restricted: false },
  ];
}
